import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogTitle, DialogContent, IconButton, Button, Box, useTheme, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlusMinusControl from "../../Shared/components/plusMinusControl";
import { proceedWithSaveChanges } from "../utils/proceedWithSaveChanges";
import Typography from "@mui/material/Typography";
import { useMyMealsStyles } from "../hooks/useMyMealsStyles";
import moment from "moment";
import { MyMealsQuantitySelectionMode } from "../../Account/Subscriptions/subscriptionsUtils/subscriptions-types";
import { ExtraSmallFab } from "../../Shared/components/Buttons/ExtraSmallFAB";
import "../utils/myMealsCSS.css";
import { useCompanyConfigQuery } from "../../SWR/Company/useCompanyConfigQuery";

EditMealDialog.propTypes = {
  handleMealQuantityChange: PropTypes.func.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  programMealId: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mealName: PropTypes.string.isRequired,
  dayOfWeek: PropTypes.string.isRequired,
  existingQuantity: PropTypes.number.isRequired,
  addMealsEnabled: PropTypes.bool.isRequired,
  removeMealsEnabled: PropTypes.bool.isRequired,
  recipes: PropTypes.array,
  myMealsConfig: PropTypes.object,
  handleRecipeQuantityChange: PropTypes.func.isRequired,
  day: PropTypes.object.isRequired,
  days: PropTypes.array,
  mealQuantityPerDay: PropTypes.object,
  updateMealQuantityPerDay: PropTypes.func,
  selectedOrderMealQuantity: PropTypes.number,
  updateSelectedOrderMealQuantity: PropTypes.func,
  amountsPerDay: PropTypes.object,
  updateAmountsPerDay: PropTypes.func,
  amountForOrder: PropTypes.number,
  updateAmountForOrder: PropTypes.func,
  totalQuantitiesPerDay: PropTypes.object,
  updateTotalQuantitiesPerDay: PropTypes.func,
  orderType: PropTypes.string,
};

export default function EditMealDialog(props) {
  const [mealToEditNewQuantity, setMealToEditNewQuantity] = useState(props.existingQuantity);
  const {
    myMealsConfig,
    day,
    days,
    mealQuantityPerDay,
    amountsPerDay,
    amountForOrder,
    selectedOrderMealQuantity,
    updateAmountsPerDay,
    updateAmountForOrder,
    updateMealQuantityPerDay,
    updateSelectedOrderMealQuantity,
    totalQuantitiesPerDay,
    updateTotalQuantitiesPerDay,
    orderType,
  } = props;
  const currentDate = day.DateFormatted;
  const { classes } = useMyMealsStyles();
  const theme = useTheme();
  const { myMealsCompanyText } = useCompanyConfigQuery();

  let currentMealPrice = props.day.Meals.find((meal) => meal.Name === props.mealName).Price;
  currentMealPrice = currentMealPrice ? currentMealPrice : 0.0;

  const handleSetMealToEditNewQuantity = (quantity, addOrSubValue) => {
    let newMealQuantityPerDay = mealQuantityPerDay;
    let newTotalQuantities = totalQuantitiesPerDay;

    let newAmountsPerDay = amountsPerDay;
    let newAmountForOrder = amountForOrder + addOrSubValue * currentMealPrice;
    let currentMealQuantityForDay = mealQuantityPerDay[currentDate] ? mealQuantityPerDay[currentDate] : 0;
    let currentAmountForDay = amountsPerDay[currentDate] ? amountsPerDay[currentDate] : 0;
    let totalQuantityForDay = newTotalQuantities[currentDate] ? newTotalQuantities[currentDate] : 0;

    newMealQuantityPerDay[currentDate] = currentMealQuantityForDay + addOrSubValue;
    newAmountsPerDay[currentDate] = currentAmountForDay + addOrSubValue * currentMealPrice;

    newTotalQuantities[currentDate] = totalQuantityForDay + addOrSubValue;

    updateMealQuantityPerDay(newMealQuantityPerDay);
    updateSelectedOrderMealQuantity(props.selectedOrderMealQuantity + addOrSubValue);
    updateTotalQuantitiesPerDay(newTotalQuantities);
    updateAmountsPerDay(newAmountsPerDay);
    updateAmountForOrder(newAmountForOrder);

    setMealToEditNewQuantity(quantity + addOrSubValue);
  };

  useEffect(() => {
    setMealToEditNewQuantity(props.existingQuantity);
  }, [props.existingQuantity]);

  let handleConfirmClick = () => {
    if (props.myMealsConfig.IsSingleRecipeSelection) {
      props.handleMealQuantityChange(
        props.programMealId,
        mealToEditNewQuantity,
        props.myMealsConfig.IsSingleRecipeSelection
      );

      props.recipes.map((recipe) => {
        if (recipe.Quantity > 0) {
          props.handleRecipeQuantityChange(recipe.RecipeId, props.programMealId, mealToEditNewQuantity);
        }
      });
    } else {
      props.handleMealQuantityChange(props.programMealId, mealToEditNewQuantity);
    }

    props.setIsOpen(false);
  };

  let editQuantityDisabledMessage = [];
  let isAddDisabled = false;
  let maxPerMealType = props.myMealsConfig != null ? props.myMealsConfig.MaxPerMealType : 99;
  let maxPerMealTypeWarningText = props.myMealsConfig != null ? props.myMealsConfig.MaxPerMealTypeWarningText : "";
  if (mealToEditNewQuantity >= maxPerMealType) {
    isAddDisabled = true;
    editQuantityDisabledMessage.push({
      day: day.DateFormatted,
      warningText: maxPerMealTypeWarningText.replace("{mealName}", props.mealName),
    });
  }

  if (!props.addMealsEnabled && mealToEditNewQuantity >= props.existingQuantity) {
    isAddDisabled = true;
    editQuantityDisabledMessage = [];
  }

  let saveChangesStatus = proceedWithSaveChanges(
    myMealsConfig,
    days,
    mealQuantityPerDay,
    selectedOrderMealQuantity,
    amountsPerDay,
    amountForOrder,
    totalQuantitiesPerDay,
    orderType
  );

  if (!saveChangesStatus.proceed) {
    editQuantityDisabledMessage = editQuantityDisabledMessage.concat(saveChangesStatus.dayWarningMessages);
  }

  let {
    myMealsConfig: { RestrictAddRemoveDays, MealQuantitySelectionMode },
  } = props;

  let canRemoveMeals =
    mealToEditNewQuantity > 0 && (props.removeMealsEnabled || mealToEditNewQuantity > props.existingQuantity);

  const restrictMealQuantitySelection = MealQuantitySelectionMode === MyMealsQuantitySelectionMode.NoChanges;

  let noRestrictions = !RestrictAddRemoveDays && !restrictMealQuantitySelection;

  let numberOfMeals = day.Meals.reduce((total, meal) => total + meal.Quantity, 0);

  let restrictRemoveDay = RestrictAddRemoveDays && mealToEditNewQuantity == 1 && numberOfMeals === 1;

  // Only the warning for the current day should be shown in the modal
  const warningForDay =
    editQuantityDisabledMessage &&
    editQuantityDisabledMessage.find((warning) => moment(warning.day).isSame(day.DateFormatted));

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      className={`${classes.body} ${classes.editMealDialog}`}
      maxWidth="sm"
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h3">{props.mealName && props.mealName}</Typography>
          <ExtraSmallFab icon={CloseIcon} aria-label="close" onClick={props.onClose} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box style={{ textAlign: "center" }}>{myMealsCompanyText["EditMealQuantityModal"]}</Box>
          <Box style={{ marginTop: "10px", fontWeight: "bold" }}>Amount For {props.dayOfWeek}</Box>
          <PlusMinusControl
            style={{
              padding: "10px",
            }}
            handleAddClick={() => handleSetMealToEditNewQuantity(mealToEditNewQuantity, 1)}
            handleMinusClick={() => handleSetMealToEditNewQuantity(mealToEditNewQuantity, -1)}
            quantity={mealToEditNewQuantity}
            isAddDisabled={isAddDisabled}
            isMinusDisabled={!canRemoveMeals || restrictRemoveDay}
          />
          {warningForDay && <Typography>{warningForDay.warningText}</Typography>}
          {props.removeMealsEnabled && props.existingQuantity > 0 && noRestrictions && (
            <>
              {mealToEditNewQuantity > 0 ? (
                <Box className="my-meals-skip-button" onClick={() => setMealToEditNewQuantity(0)}>
                  Skip {props.mealName} Today
                </Box>
              ) : (
                <Box className="my-meals-skip-button">{props.mealName} Will Be Skipped</Box>
              )}
            </>
          )}
          {restrictRemoveDay && <Typography>You cannot remove this day from your order.</Typography>}
        </Box>
      </DialogContent>
      <Box
        sx={{
          padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        }}
      >
        <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="center">
          <Typography>Your changes will be finalized in the cart.</Typography>
          <Button
            variant="contained"
            className="mm-edit-meal-confirm-button" // secondary color
            disabled={props.existingQuantity === mealToEditNewQuantity}
            onClick={handleConfirmClick}
          >
            Confirm
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}
